import React from 'react'
import { Form } from 'react-bootstrap'

/**
 * Normalises the option shapes already used across the panel:
 *   { _id, name }  API records
 *   { value, label }  constant lists
 *   'RUNNING'  bare strings
 */
const normaliseOption = (option) => {
  if (option === null || option === undefined) return null
  if (typeof option === 'string' || typeof option === 'number') {
    return { value: option, label: String(option) }
  }
  return {
    value: option.value !== undefined ? option.value : option._id,
    label: option.label !== undefined ? option.label : option.name,
  }
}

/**
 * Label, control, helper text and error state in one place, so a field looks
 * the same in every modal. Every control is the solid style — the system has
 * no bordered inputs.
 *
 * Pass `children` to supply a control the system doesn't cover (a date picker,
 * a react-select, a chip group) and still get the label, hint and error frame.
 */
const FormField = ({
  label,
  required = false,
  hint,
  error,
  as = 'input',
  type = 'text',
  options,
  placeholder,
  value,
  onChange,
  disabled = false,
  name,
  id,
  className = '',
  span = false,
  children,
  emptyLabel,
  ...rest
}) => {
  const controlId = id || (name ? `field-${name}` : undefined)

  const renderControl = () => {
    if (children) return children

    if (as === 'select') {
      return (
        <Form.Select
          id={controlId}
          className='form-select-solid'
          name={name}
          value={value}
          onChange={onChange}
          disabled={disabled}
          required={required}
          {...rest}
        >
          {emptyLabel !== null && (
            <option value=''>{emptyLabel || `Select ${label || ''}`.trim()}</option>
          )}
          {(options || []).map(normaliseOption).map(
            (option) =>
              option && (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ),
          )}
        </Form.Select>
      )
    }

    return (
      <Form.Control
        id={controlId}
        className='form-control-solid'
        as={as === 'textarea' ? 'textarea' : undefined}
        type={as === 'textarea' ? undefined : type}
        name={name}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        disabled={disabled}
        required={required}
        {...rest}
      />
    )
  }

  return (
    <div
      className={[
        'app-modal__field',
        error ? 'app-modal__field--invalid' : '',
        span ? 'app-modal__grid-span' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {label && (
        <label className='app-modal__label' htmlFor={controlId}>
          {label}
          {required && <span className='app-modal__required'>*</span>}
        </label>
      )}
      {renderControl()}
      {(error || hint) && (
        <span className={`app-modal__help ${error ? 'app-modal__help--error' : ''}`.trim()}>
          {error || hint}
        </span>
      )}
    </div>
  )
}

/**
 * The field grid. Two columns by default, one column under 768px.
 * `span` on a FormField makes it take the full width of the row.
 */
export const FieldRow = ({ cols = 2, className = '', children }) => (
  <div
    className={`app-modal__grid ${className}`.trim()}
    style={cols !== 2 ? { '--am-cols': cols } : undefined}
  >
    {children}
  </div>
)

FormField.Row = FieldRow

export default FormField
